import { createUseStyles } from "react-jss"
import { titleMarginBottom } from "../constants/layout"
import FullScreenLoadingIndicator from "../components/FullScreenLoadingIndicator"
import { useEffect, useState } from "react"
import { useAppSelector } from "../redux/hooks"
import useCreditOverviewActions, { CreditOverviewItemModel } from "../hooks/useCreditOverviewActions"
import CreditOverviewList from "../components/CreditOverview/CreditOverviewList"
import CardSection from "../components/CardSection"
import DetailsCard from "../components/DetailsCard"
import { Money } from "../redux/types"
import { formatMoney } from "../utils/moneyHelper"
import DatePicker from "../components/DatePicker"

const CreditOverview = () => {
    const styles = useStyles()

    const loadingCreditUsage = useAppSelector(state => state.creditOverview.loadingCreditUsage)
    const loadingCreditScore = useAppSelector(state => state.creditOverview.loadingCreditScore)
    const creditUsageOverview = useAppSelector(state => state.creditOverview.creditUsageOverview)
    const creditScoreOverview = useAppSelector(state => state.creditOverview.creditScoreOverview)

    const [overviewItems, setOverviewItems] = useState<CreditOverviewItemModel[]>()
    const [aggregatedValues, setAggregatedValues] = useState<{currentRiskedAmount: Money, recentMaxRiskedAmount: Money, approvedCredit: Money, availableCredit: Money, currentUsage: Money, recentMaxUsage: Money}>()
    const [date, setDate] = useState<string>()

    const {
      getCreditUsageOverview, 
      getCreditScoreOverview, 
      combine} = useCreditOverviewActions()

    useEffect(() => {
      getCreditUsageOverview(date ?? null)
      getCreditScoreOverview(date ?? null)
    }, [date ?? null])

    useEffect(() => {
      if (creditUsageOverview.length > 0 && creditScoreOverview.length > 0) {
        const items = combine(creditUsageOverview, creditScoreOverview)
        setOverviewItems(items)

        if (items.length > 0) {
          setAggregatedValues({
            approvedCredit: { amount: Math.round(items.reduce((acc, x) => acc + x.approvedCreditLimit?.amount, 0)), currencyCode: items[0].approvedCreditLimit?.currencyCode },
            availableCredit: { amount: Math.round(items.reduce((acc, x) => acc + x.availableCreditLimit?.amount, 0)), currencyCode: items[0].availableCreditLimit?.currencyCode },
            currentUsage: { amount: Math.round(items.reduce((acc, x) => acc + x.currentCreditUsage.amount, 0)), currencyCode: items[0].currentCreditUsage.currencyCode },
            recentMaxUsage: { amount: Math.round(items.reduce((acc, x) => acc + x.recentMaxCreditUsage.amount, 0)), currencyCode: items[0].recentMaxCreditUsage.currencyCode },
            currentRiskedAmount: { amount: Math.round(items.reduce((acc, x) => acc + x.currentRiskedAmount.amount, 0)), currencyCode: items[0].currentRiskedAmount.currencyCode },
            recentMaxRiskedAmount: { amount: Math.round(items.reduce((acc, x) => acc + x.recentMaxRiskedAmount.amount, 0)), currencyCode: items[0].recentMaxRiskedAmount.currencyCode },
          })
        }
      }
    }, [creditUsageOverview, creditScoreOverview])

    return (
      <div className={styles.container}>
          <h1>Credit overview</h1>
          <CardSection>
            <DetailsCard>
               <div style={{width: 200, marginBottom: 20}}>
                <DatePicker
                  id='date'
                  label='Date'
                  includeTime={false}
                  value={date}
                  placeholder='Today'
                  onChange={value => {
                      setDate(value ?? undefined)
                  }}
                />
              </div>
              <h6>Aggregated summary</h6>
              <p>Approved credit: <span>{aggregatedValues?.approvedCredit != null ? formatMoney(aggregatedValues?.approvedCredit) : '-'}</span></p>
              <p>Available credit: <span>{aggregatedValues?.availableCredit != null ? formatMoney(aggregatedValues?.availableCredit) : '-'}</span></p>
              <p>Current usage: <span>{aggregatedValues?.currentUsage != null ? formatMoney(aggregatedValues?.currentUsage) : '-'}</span></p>
              <p>Recent max usage: <span>{aggregatedValues?.recentMaxUsage != null ? formatMoney(aggregatedValues?.recentMaxUsage) : '-'}</span></p>
              <p>Current risked amount: <span>{aggregatedValues?.currentRiskedAmount != null ? formatMoney(aggregatedValues?.currentRiskedAmount) : '-'}</span></p>
              <p>Recent max risked amount: <span>{aggregatedValues?.recentMaxRiskedAmount != null ? formatMoney(aggregatedValues?.recentMaxRiskedAmount) : '-'}</span></p>
            </DetailsCard>
          </CardSection>
          <CreditOverviewList items={overviewItems ?? []} />
          <FullScreenLoadingIndicator visible={loadingCreditUsage || loadingCreditScore} />
      </div>
      )
}

export default CreditOverview

const useStyles = createUseStyles({
  container: {
    '& h1': {
      marginBottom: titleMarginBottom
    }
  },
  listContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    textAlign: 'left',
    transition: '0.4s'
  },
  listCell: {
    flex: 1
  }
})
